import React, { useCallback, useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useAppContext } from '../../../../../redux/slices/appContext'
import { postNamedCommand } from '../../../../../service'
import SydModal from '../../../../commonDesign/SydModal'
import SydModalActions from '../../../../commonDesign/SydModal/SydModalActions'
import SydButton from '../../../../commonDesign/Button'
import Markup from '../../../../atoms/Markup'
import {
  labelStyle, valueStyle as baseValueStyle, describeRecurrence
} from './redtailUIHelpers'
import { LinkedContactDetail } from './LinkedContactDisplay'
import CompleteActivityButton from './CompleteActivityButton'
import { toLocal, LOCAL_TZ_ABBR, toDatetimeLocalValue } from './activityDateHelpers'
import useActivityCodes from './useActivityCodes'
import ActivityForm from './ActivityForm'

const MODE_VIEWING = 'viewing'
const MODE_EDITING = 'editing'
const MODE_CONFIRMING = 'confirming'
const MODE_EDIT_SCOPE = 'edit-scope'
const MODE_DELETE_SCOPE = 'delete-scope'

function isRecurring (activity) {
  return (activity?.repeats && activity.repeats !== 'never') || !!activity?.recurrence_period
}

function getInstanceId (activity) {
  if (!activity?.start_date) return null
  return dayjs(activity.start_date).unix()
}

const valueStyle = { ...baseValueStyle, marginBottom: '12px' }

function formatDetailDateTime (isoString, allDay) {
  if (!isoString) return null
  const local = toLocal(isoString)
  if (allDay) return local.format('ddd, MMM D, YYYY')
  const tzSuffix = LOCAL_TZ_ABBR ? ` ${LOCAL_TZ_ABBR}` : ''
  return `${local.format('ddd, MMM D, YYYY h:mma')}${tzSuffix}`
}

function buildContactOptions (activity) {
  if (!activity.linked_contacts?.length) return []
  return activity.linked_contacts.map(c => ({
    label: c.full_name || `Contact ${c.contact_id ?? c.id}`,
    value: c.contact_id ?? c.id,
    hoh: !!c.hoh
  }))
}

function computeDuration (activity) {
  if (!activity.start_date || !activity.end_date) return 60
  const diff = dayjs(activity.end_date).diff(dayjs(activity.start_date), 'minute')
  if (diff <= 0) return 60
  return diff
}

function buildFormDefaults (activity, formMode) {
  const allDay = formMode === 'simple' ? true : (activity.all_day || false)
  return {
    subject: activity.subject || '',
    start_date: toDatetimeLocalValue(activity.start_date, allDay),
    end_date: toDatetimeLocalValue(activity.end_date, allDay),
    duration: allDay ? 60 : computeDuration(activity),
    all_day: allDay,
    contacts: buildContactOptions(activity),
    location: activity.location || '',
    description: activity.description || '',
    priority: activity.priority ?? 1,
    importance: activity.importance ?? undefined,
    activity_code_id: activity.activity_code_id ?? undefined,
    repeats: activity.repeats || 'never',
    category_id: activity.category_id ?? ''
  }
}

/* ── Read-only view ────────────────────────────────────────────────── */

function ViewingBody ({ activity, categoryOptions, formMode }) {
  const { clientId } = useAppContext()
  const { getLabel: getActivityCodeLabel } = useActivityCodes(clientId)
  const isCalendar = formMode === 'calendar'

  const startFormatted = isCalendar
    ? formatDetailDateTime(activity.start_date, activity.all_day)
    : activity.start_date ? dayjs(activity.start_date).format('ddd, MMM D, YYYY') : null

  const endFormatted = isCalendar
    ? formatDetailDateTime(activity.end_date, activity.all_day)
    : activity.end_date ? dayjs(activity.end_date).format('ddd, MMM D, YYYY') : null

  const categoryName = useMemo(() => {
    if (!activity.category_id) return null
    const cat = categoryOptions.find(c => c.value === activity.category_id)
    return cat?.label ?? null
  }, [activity.category_id, categoryOptions])

  const activityTypeName = useMemo(
    () => getActivityCodeLabel(activity.activity_code_id),
    [activity.activity_code_id, getActivityCodeLabel]
  )

  const repeatsLabel = useMemo(() => describeRecurrence(activity), [activity])
  const isCompleted = !!activity.completed

  const importanceLabel = useMemo(() => {
    const map = { 1: 'Low', 2: 'Normal', 3: 'High' }
    return map[activity.importance] ?? null
  }, [activity.importance])

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '24px' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <LinkedContactDetail
            contacts={activity.linked_contacts}
            labelStyle={labelStyle}
            valueStyle={valueStyle}
          />
        </div>
        <div style={{ display: 'flex', gap: '24px', flexShrink: 0 }}>
          {startFormatted ? (
            <div>
              <div style={labelStyle}>{isCalendar ? 'Start' : 'Start Date'}</div>
              <div style={valueStyle}>{startFormatted}</div>
            </div>
          ) : null}
          {endFormatted ? (
            <div>
              <div style={labelStyle}>{isCalendar ? 'End' : 'Due Date'}</div>
              <div style={valueStyle}>{endFormatted}</div>
            </div>
          ) : null}
        </div>
      </div>

      {isCalendar && activity.all_day ? (
        <div style={{ ...valueStyle, fontStyle: 'italic', color: 'var(--text-color-subtle)' }}>
          All-day event
        </div>
      ) : null}

      {activityTypeName ? (
        <>
          <div style={labelStyle}>Type</div>
          <div style={valueStyle}>{activityTypeName}</div>
        </>
      ) : null}

      {activity.priority ? (
        <>
          <div style={labelStyle}>Priority</div>
          <div style={valueStyle}>{activity.priority}</div>
        </>
      ) : null}

      {importanceLabel ? (
        <>
          <div style={labelStyle}>Importance</div>
          <div style={valueStyle}>{importanceLabel}</div>
        </>
      ) : null}

      {activity.location ? (
        <>
          <div style={labelStyle}>Location</div>
          <div style={valueStyle}>{activity.location}</div>
        </>
      ) : null}

      {categoryName ? (
        <>
          <div style={labelStyle}>Category</div>
          <div style={valueStyle}>{categoryName}</div>
        </>
      ) : null}

      <div style={labelStyle}>Description</div>
      {activity.description ? (
        <div style={valueStyle}>
          <Markup value={activity.description} type='html' style={{ fontSize: 'inherit' }} />
        </div>
      ) : (
        <div style={{ ...valueStyle, color: 'var(--text-color-subtle)', fontStyle: 'italic' }}>
          No description
        </div>
      )}

      {isCalendar && repeatsLabel ? (
        <>
          <div style={labelStyle}>Repeats</div>
          <div style={valueStyle}>{repeatsLabel}</div>
        </>
      ) : null}

      {activity.attendees?.length ? (
        <>
          <div style={labelStyle}>Assigned To</div>
          <div style={valueStyle}>
            {activity.attendees.map((a, i) => {
              const name = a.full_name || a.name || `User ${a.user_id ?? a.id ?? i + 1}`
              return (
                <div key={a.id ?? a.user_id ?? i} style={{ fontSize: 'var(--font-size-sm)' }}>
                  {name}
                </div>
              )
            })}
          </div>
        </>
      ) : null}

      {isCompleted ? (
        <div style={{
          fontSize: 'var(--font-size-sm)',
          color: 'var(--color-growth-500)',
          fontWeight: 600,
          marginBottom: '8px'
        }}
        >
          This activity has been completed
        </div>
      ) : null}
    </div>
  )
}

ViewingBody.propTypes = {
  activity: PropTypes.object.isRequired,
  categoryOptions: PropTypes.array,
  formMode: PropTypes.string
}

/* ── Delete confirmation ───────────────────────────────────────────── */

function ConfirmDeleteBody ({ activity, occurrenceScope, onDeleted, onCancel, invalidateQueryKeys }) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()

  const { mutate: handleDelete, isLoading: deleting, error } = useMutation({
    mutationFn: () => {
      if (occurrenceScope === 'instance') {
        const instanceId = getInstanceId(activity)
        return postNamedCommand('coreData', 'execute-redtail-domain-command', {
          command: 'removeActivityInstance',
          autoResolveConnection: true,
          clientId,
          request: { activityId: activity.id, instanceId }
        })
      }
      return postNamedCommand('coreData', 'execute-redtail-domain-command', {
        command: 'removeActivity',
        autoResolveConnection: true,
        clientId,
        request: { activityId: activity.id }
      })
    },
    onSuccess: () => {
      onDeleted()
      if (invalidateQueryKeys) {
        for (const key of invalidateQueryKeys) {
          queryClient.invalidateQueries({ queryKey: key })
        }
      }
    }
  })

  const deleteError = error?.message || null

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ fontSize: 'var(--font-size-sm)', margin: 0 }}>
        {occurrenceScope === 'instance'
          ? <>Are you sure you want to delete this occurrence of <strong>{activity.subject || 'this activity'}</strong>?</>
          : <>Are you sure you want to delete <strong>{activity.subject || 'this activity'}</strong>? This action cannot be undone.</>}
      </p>

      {deleteError ? (
        <div style={{ color: 'var(--color-decline-500)', fontSize: 'var(--font-size-xs)' }}>
          {deleteError}
        </div>
      ) : null}

      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel} disabled={deleting}>
          Go Back
        </SydButton>
        <SydButton
          variant='primary'
          size='sm'
          processing={deleting}
          onClick={() => handleDelete()}
          style={{ backgroundColor: 'var(--color-decline-500)' }}
        >
          Delete
        </SydButton>
      </SydModalActions>
    </div>
  )
}

ConfirmDeleteBody.propTypes = {
  activity: PropTypes.object.isRequired,
  occurrenceScope: PropTypes.string,
  onDeleted: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array)
}

/* ── Recurrence scope choice ───────────────────────────────────────── */

function RecurrenceChoiceBody ({ action, onChooseAll, onChooseInstance, onCancel }) {
  const actionLabel = action === 'edit' ? 'edit' : 'delete'
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
      <p style={{ fontSize: 'var(--font-size-sm)', margin: 0 }}>
        This is a recurring activity. Would you like to {actionLabel} this occurrence or all occurrences?
      </p>
      <SydModalActions>
        <SydButton variant='ghost' size='sm' onClick={onCancel}>
          Cancel
        </SydButton>
        <SydButton variant='outline' size='sm' onClick={onChooseInstance}>
          This Occurrence
        </SydButton>
        <SydButton variant='primary' size='sm' onClick={onChooseAll}>
          All Occurrences
        </SydButton>
      </SydModalActions>
    </div>
  )
}

RecurrenceChoiceBody.propTypes = {
  action: PropTypes.oneOf(['edit', 'delete']).isRequired,
  onChooseAll: PropTypes.func.isRequired,
  onChooseInstance: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired
}

/* ── Main modal ────────────────────────────────────────────────────── */

function ActivityDetailModal ({
  open,
  onClose,
  activity,
  config,
  members,
  enableEdit = false,
  formMode = 'calendar',
  invalidateQueryKeys,
  categoryOptions = []
}) {
  const { clientId } = useAppContext()
  const queryClient = useQueryClient()
  const [mode, setMode] = useState(MODE_VIEWING)
  const [occurrenceScope, setOccurrenceScope] = useState('all')
  const [bladeOpen, setBladeOpen] = useState(false)

  useEffect(() => {
    if (open) {
      setMode(MODE_VIEWING)
      setOccurrenceScope('all')
      setBladeOpen(false)
    }
  }, [open])

  const recurring = activity && isRecurring(activity)
  const isCompleted = !!activity?.completed

  const handleClose = useCallback(() => {
    setMode(MODE_VIEWING)
    setBladeOpen(false)
    onClose()
  }, [onClose])

  const handleEditClick = useCallback(() => {
    if (recurring) {
      setMode(MODE_EDIT_SCOPE)
    } else {
      setOccurrenceScope('all')
      setMode(MODE_EDITING)
    }
  }, [recurring])

  const handleDeleteClick = useCallback(() => {
    if (recurring) {
      setMode(MODE_DELETE_SCOPE)
    } else {
      setOccurrenceScope('all')
      setMode(MODE_CONFIRMING)
    }
  }, [recurring])

  const { mutateAsync: handleEditSubmit } = useMutation({
    mutationFn: (body) => {
      if (occurrenceScope === 'instance') {
        const instanceId = getInstanceId(activity)
        return postNamedCommand('coreData', 'execute-redtail-domain-command', {
          command: 'modifyActivityInstance',
          autoResolveConnection: true,
          clientId,
          request: { activityId: activity.id, instanceId, body }
        })
      }
      return postNamedCommand('coreData', 'execute-redtail-domain-command', {
        command: 'modifyActivity',
        autoResolveConnection: true,
        clientId,
        request: { activityId: activity.id, body }
      })
    },
    onSuccess: () => {
      handleClose()
      if (invalidateQueryKeys) {
        for (const key of invalidateQueryKeys) {
          queryClient.invalidateQueries({ queryKey: key })
        }
      }
    }
  })

  if (!activity) return null

  const titles = {
    [MODE_VIEWING]: activity.subject || 'Activity Details',
    [MODE_EDITING]: 'Edit Activity',
    [MODE_CONFIRMING]: 'Delete Activity',
    [MODE_EDIT_SCOPE]: 'Edit Activity',
    [MODE_DELETE_SCOPE]: 'Delete Activity'
  }
  const title = titles[mode]

  const isCalendar = formMode === 'calendar'
  const showBlade = isCalendar && bladeOpen

  return (
    <SydModal
      open={open}
      onClose={handleClose}
      title={title}
      size={showBlade ? 'large' : 'xmedium'}
      disablePortal={false}
    >
      {mode === MODE_VIEWING && (
        <>
          <ViewingBody
            activity={activity}
            categoryOptions={categoryOptions}
            formMode={formMode}
          />
          <SydModalActions>
            {enableEdit ? (
              <SydButton
                variant='outline'
                size='sm'
                onClick={handleDeleteClick}
                style={{ color: 'var(--color-decline-500)', borderColor: 'var(--color-decline-500)' }}
              >
                Delete
              </SydButton>
            ) : null}
            <div style={{ flex: 1 }} />
            <SydButton variant='ghost' size='sm' onClick={handleClose}>
              Close
            </SydButton>
            {enableEdit ? (
              <SydButton variant='outline' size='sm' icon='edit' onClick={handleEditClick}>
                Edit
              </SydButton>
            ) : null}
            {!isCompleted ? (
              <CompleteActivityButton
                activityId={activity.id}
                onCompleted={handleClose}
                invalidateQueryKeys={invalidateQueryKeys}
                variant='primary'
                size='sm'
              >
                Complete
              </CompleteActivityButton>
            ) : null}
          </SydModalActions>
        </>
      )}

      {mode === MODE_EDIT_SCOPE && (
        <RecurrenceChoiceBody
          action='edit'
          onChooseAll={() => { setOccurrenceScope('all'); setMode(MODE_EDITING) }}
          onChooseInstance={() => { setOccurrenceScope('instance'); setMode(MODE_EDITING) }}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}

      {mode === MODE_DELETE_SCOPE && (
        <RecurrenceChoiceBody
          action='delete'
          onChooseAll={() => { setOccurrenceScope('all'); setMode(MODE_CONFIRMING) }}
          onChooseInstance={() => { setOccurrenceScope('instance'); setMode(MODE_CONFIRMING) }}
          onCancel={() => setMode(MODE_VIEWING)}
        />
      )}

      {mode === MODE_EDITING && (
        <ActivityForm
          defaultValues={buildFormDefaults(activity, formMode)}
          config={config}
          members={members}
          categoryOptions={categoryOptions}
          onSubmit={handleEditSubmit}
          onCancel={() => { setBladeOpen(false); setMode(MODE_VIEWING) }}
          submitLabel='Save Changes'
          cancelVariant='ghost'
          formMode={formMode}
          showTypeField
          bladeOpen={bladeOpen}
          onBladeChange={setBladeOpen}
        />
      )}

      {mode === MODE_CONFIRMING && (
        <ConfirmDeleteBody
          activity={activity}
          occurrenceScope={occurrenceScope}
          onDeleted={handleClose}
          onCancel={() => setMode(MODE_VIEWING)}
          invalidateQueryKeys={invalidateQueryKeys}
        />
      )}
    </SydModal>
  )
}

ActivityDetailModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activity: PropTypes.object,
  config: PropTypes.object,
  members: PropTypes.array,
  enableEdit: PropTypes.bool,
  formMode: PropTypes.oneOf(['calendar', 'simple']),
  invalidateQueryKeys: PropTypes.arrayOf(PropTypes.array),
  categoryOptions: PropTypes.array
}

export default ActivityDetailModal
